/**
 * Report tab: the summary counts inline, the small health table (group
 * header, units and components with status and health bar) and the buttons
 * that open the full system report, the health dashboard and the
 * maintenance history dialogs.
 */
import { useMemo } from 'react';
import { ClipboardList, HeartPulse, ScrollText } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { useTwin } from '../../store';
import { useInventoryView } from '../adapters/inventory';
import { buildReportModel } from '../adapters/report';
import { StatusIcon } from '../components/badges';
import { LinearGauge } from '../components/bars';
import { Button, EmptyState, SectionTitle, Stat } from '../components/layout';
import { useNowMs } from '../hooks/useNowMs';
import { pathTo } from '../lib/cardTree';
import { useTwinUi } from '../state';
import { isModelOnlyElement } from '../../data/modelParts';
import { ReferenceSpecs } from '../components/ReferenceSpecs';

export function ReportTab() {
  const element = useTwin((s) => s.element);
  const readings = useTwin((s) => s.readings);
  const history = useTwin((s) => s.history);
  const inventory = useInventoryView();
  const openDialog = useTwinUi((s) => s.openDialog);
  const select = useUI((s) => s.select);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const now = useNowMs(60_000);

  const model = useMemo(
    () => (element ? buildReportModel(element, { readings, history, inventory: inventory.manager, nowMs: now }) : null),
    [element, readings, history, inventory.manager, now],
  );

  if (!element || !model) return <EmptyState title="No twin loaded." className="m-3" />;
  if (isModelOnlyElement(element)) return <div className="p-3 text-xs"><SectionTitle>Spacecraft reference</SectionTitle><p className="mb-2 text-ink-3">Published specifications are available in Health and Parts. Operational health, lifetime and replacement forecasts require connected telemetry.</p>{element.subsystems.filter((s) => s.reference).map((s) => <ReferenceSpecs key={s.id} subsystem={s} />)}</div>;
  const { counts } = model;

  const view = (id: string) => {
    setExpanded(pathTo(element.subsystems, id).map((s) => s.id), true);
    select({ subsystemId: id });
  };

  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Components" value={counts.total} />
        <Stat label="Operational" value={counts.operational} tone="nominal" />
        <Stat label="Warning" value={counts.warning} tone={counts.warning > 0 ? 'caution' : undefined} />
        <Stat label="Critical" value={counts.critical} tone={counts.critical > 0 ? 'warning' : undefined} />
      </div>
      <div className="flex flex-wrap gap-1">
        <Button primary onClick={() => openDialog('report')}>
          <ClipboardList className="h-3.5 w-3.5" /> Full report
        </Button>
        <Button onClick={() => openDialog('health')}>
          <HeartPulse className="h-3.5 w-3.5" /> Health dashboard
        </Button>
        <Button onClick={() => openDialog('history')}>
          <ScrollText className="h-3.5 w-3.5" /> Maintenance history
        </Button>
      </div>
      <div>
        <SectionTitle>System health report</SectionTitle>
        {model.groups.length === 0 ? (
          <EmptyState title="No physical components on this element." />
        ) : (
          <div className="overflow-hidden rounded-control border border-hairline">
            {model.groups.map((group) => (
              <div key={group.id}>
                <div className="bg-inset/60 px-2 py-1 text-[10px] uppercase tracking-wider text-ink-3">{group.name}</div>
                {group.units.map(({ row, components }) => (
                  <div key={row.id}>
                    <ReportLine id={row.id} name={row.name} level={row.level} colour={row.colour} health={row.health} depth={0} count={components.length} onView={view} />
                    {components.map((c) => (
                      <ReportLine key={c.id} id={c.id} name={c.name} level={c.level} colour={c.colour} health={c.health} depth={c.itemType === 'subcomponent' ? 2 : 1} onView={view} />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportLine({ id, name, level, colour, health, depth, count, onView }: { id: string; name: string; level: string; colour: 'green' | 'yellow' | 'red'; health: number; depth: number; count?: number; onView: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onView(id)} className={cn('flex w-full items-center gap-2 border-t border-hairline px-2 py-1 text-left hover:bg-overlay', depth === 0 && 'bg-raised')} style={{ paddingLeft: 8 + depth * 12 }}>
      <StatusIcon colour={colour} />
      <span className={cn('min-w-0 flex-1 truncate', depth === 0 ? 'text-ink' : 'text-ink-2')}>
        {name}
        {count !== undefined && count > 0 && <span className="ml-1 text-[10px] text-ink-3">{count} components</span>}
      </span>
      <span className="w-20 shrink-0 truncate text-right text-[10px] uppercase tracking-wider text-ink-3">{level}</span>
      <LinearGauge value={health} className="w-24 shrink-0" />
    </button>
  );
}
